import React from "react";

// The proportion (width / height) a crop comes out with. The four corners straighten
// the face, but a face turned away from the camera stays foreshortened, so the admin
// picks the real proportion here and the API crops to it (aspect + aspect_source on
// POST /admin/cover-curation/:id/faces/:face/crop).
//
// Points are [x, y] in 0..1, clockwise from top-left; size is the source image's
// { width, height } in pixels.

const px = (points, size) => points.map(([x, y]) => [x * size.width, y * size.height]);
const sub = (a, b) => [a[0] - b[0], a[1] - b[1]];
const distance = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);

// The quad's own edge lengths: what the crop gives without a chosen proportion.
export function croppedAspect(points, size) {
  const c = px(points, size);
  return Math.max(distance(c[0], c[1]), distance(c[3], c[2])) / Math.max(distance(c[0], c[3]), distance(c[1], c[2]));
}

// One box corner, seen without much perspective (what LaunchBox's box renders are):
// the width, height and depth edges leaving it, in pixels. The rows of a rotation are
// orthonormal, which gives two linear equations in 1/length² of each edge; their cross
// product solves them, so the true lengths come out up to scale.
function cornerLengths(edges) {
  const r1 = edges.map(([x, y]) => x * y);
  const r2 = edges.map(([x, y]) => x * x - y * y);
  const a = [r1[1] * r2[2] - r1[2] * r2[1], r1[2] * r2[0] - r1[0] * r2[2], r1[0] * r2[1] - r1[1] * r2[0]];
  const sign = Math.sign(a[0]);
  if (sign === 0 || !a.every((value) => Math.sign(value) === sign)) return null;
  return a.map((value) => 1 / Math.sqrt(Math.abs(value)));
}

// Front and spine cropped from the same image share an edge, so each shared corner shows
// all three box edges. Returns the front's width / height and the spine's depth / height,
// averaged over the corners that solve, or null.
export function boxAspects(frontPoints, spinePoints, size) {
  const f = px(frontPoints, size);
  const s = px(spinePoints, size);
  const centerX = (quad) => quad.reduce((sum, [x]) => sum + x, 0) / 4;
  const spineOnLeft = centerX(s) < centerX(f);
  const mid = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  // [shared corner, width end, height end, depth end] — f/s: TL 0, TR 1, BR 2, BL 3.
  const corners = spineOnLeft
    ? [[mid(f[0], s[1]), f[1], f[3], s[0]], [mid(f[3], s[2]), f[2], f[0], s[3]]]
    : [[mid(f[1], s[0]), f[0], f[2], s[1]], [mid(f[2], s[3]), f[3], f[1], s[2]]];
  const solved = corners
    .map(([corner, ...ends]) => cornerLengths(ends.map((end) => sub(end, corner))))
    .filter(Boolean);
  if (solved.length === 0) return null;
  const average = (pick) => solved.reduce((sum, lengths) => sum + pick(lengths), 0) / solved.length;
  return { front: average(([w, h]) => w / h), spine: average(([, h, d]) => d / h) };
}

// One face in a photo with real perspective (Zhang & He, whiteboard rectification),
// assuming the optical center is the image center. A render with almost no perspective
// gives no usable focal length; then null.
export function perspectiveAspect(points, size) {
  const [m1, m2, m4, m3] = px(points, size).map(([x, y]) => [x - size.width / 2, y - size.height / 2, 1]);
  const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
  const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  const c14 = cross(m1, m4);
  const k2 = dot(c14, m3) / dot(cross(m2, m4), m3);
  const k3 = dot(c14, m2) / dot(cross(m3, m4), m2);
  const n2 = m2.map((v, i) => k2 * v - m1[i]);
  const n3 = m3.map((v, i) => k3 * v - m1[i]);
  const f2 = -(n2[0] * n3[0] + n2[1] * n3[1]) / (n2[2] * n3[2]);
  const diagonal = Math.hypot(size.width, size.height);
  if (!Number.isFinite(f2) || f2 <= 0 || Math.sqrt(f2) > 5 * diagonal) return null;
  const length = (n) => Math.sqrt((n[0] ** 2 + n[1] ** 2) / f2 + n[2] ** 2);
  const ratio = length(n2) / length(n3);
  return Number.isFinite(ratio) && ratio > 0 ? ratio : null;
}

const PARTNER = { front: "side", side: "front" };

// Every way to set this face's proportion, best first; ratio null = not available here.
// face: "front" | "back" | "top" | "side"; state: the curation state (faces, box).
export function aspectOptions({ face, points, size, sourceUrl, state }) {
  if (!size) return [];
  const cropped = croppedAspect(points, size);
  const options = [];

  const box = state.box;
  if (box && face !== "top") {
    // Front and back follow the crop's orientation (an N64 box is landscape in the US,
    // portrait in Japan); the spine is depth over the box's height.
    const portrait = box.height >= box.width;
    const [w, h] = (cropped < 1) === portrait ? [box.width, box.height] : [box.height, box.width];
    options.push(face === "side"
      ? { key: "platform", label: "Platform box", ratio: box.depth / box.height, detail: `${box.depth}×${box.height} mm` }
      : { key: "platform", label: "Platform box", ratio: w / h, detail: `${w}×${h} mm` });
  }

  const partner = state.faces[PARTNER[face]];
  const both = partner?.points && partner.source_url === sourceUrl
    ? (face === "front" ? boxAspects(points, partner.points, size) : boxAspects(partner.points, points, size))
    : null;
  if (both) {
    options.push({ key: "estimated", label: "Estimated", ratio: face === "front" ? both.front : both.spine, detail: `from front + spine` });
  } else if (face === "back" && state.faces.front?.aspect && state.faces.front.aspect.source !== "cropped") {
    options.push({ key: "estimated", label: "Same as front", ratio: state.faces.front.aspect.ratio, detail: state.faces.front.aspect.source });
  } else {
    const single = perspectiveAspect(points, size);
    options.push({
      key: "estimated",
      label: "Estimated",
      ratio: single,
      detail: single ? "from perspective" : face === "front" || face === "side"
        ? `crop the ${face === "front" ? "spine" : "front"} from this image first`
        : "no perspective to measure",
    });
  }

  options.push({ key: "cropped", label: "As cropped", ratio: cropped, detail: "corners only" });
  options.push({ key: "custom", label: "Custom", ratio: null, detail: null });
  return options;
}

const format = (ratio) => ratio.toFixed(3);

function CropAspectPicker({ options, choice, onChoice, custom, onCustom, busy }) {
  const customRatio = custom.width > 0 && custom.height > 0 ? custom.width / custom.height : null;
  return (
    <div className="admin-crop-aspect" role="radiogroup" aria-label="Proportion">
      <span className="admin-source-meta">Proportion</span>
      {options.map((option) => {
        const available = option.key === "custom" || option.ratio != null;
        const active = choice === option.key;
        return (
          <button
            type="button"
            key={option.key}
            role="radio"
            aria-checked={active}
            className={`admin-metadata-preset ${active ? "admin-metadata-preset--active" : ""}`}
            onClick={() => onChoice(option.key)}
            disabled={busy || !available}
            title={option.detail || undefined}
          >
            {option.label}
            {option.key === "custom"
              ? customRatio && <span className="admin-crop-aspect-value">{format(customRatio)}</span>
              : <span className="admin-crop-aspect-value">{available ? format(option.ratio) : "—"}</span>}
            {option.detail && <span className="admin-crop-aspect-detail">{option.detail}</span>}
          </button>
        );
      })}
      {choice === "custom" && (
        <span className="admin-crop-aspect-custom">
          <input
            id="crop-aspect-width"
            type="number"
            min="1"
            className="admin-tgdb-link-input"
            value={custom.width || ""}
            onChange={(e) => onCustom({ ...custom, width: Number(e.target.value) })}
            aria-label="Width"
            placeholder="W"
            disabled={busy}
          />
          ×
          <input
            id="crop-aspect-height"
            type="number"
            min="1"
            className="admin-tgdb-link-input"
            value={custom.height || ""}
            onChange={(e) => onCustom({ ...custom, height: Number(e.target.value) })}
            aria-label="Height"
            placeholder="H"
            disabled={busy}
          />
        </span>
      )}
    </div>
  );
}

// The { ratio, source } a crop is sent with; ratio null = keep the corners' own.
export function selectedAspect(options, choice, custom) {
  if (choice === "custom") {
    return custom.width > 0 && custom.height > 0 ? { ratio: custom.width / custom.height, source: "custom" } : null;
  }
  const option = options.find((candidate) => candidate.key === choice);
  if (!option || option.ratio == null || option.key === "cropped") return null;
  return { ratio: option.ratio, source: option.key };
}

// The first option that has a ratio: platform, then estimated, then as cropped.
export function defaultAspectChoice(options) {
  return options.find((option) => option.ratio != null)?.key ?? "cropped";
}

export default CropAspectPicker;
